import React from 'react';
import { motion } from 'framer-motion';
import { FaChevronRight, FaChevronLeft, FaClipboardList, FaStepForward } from 'react-icons/fa';

const easing = [0.22, 1, 0.36, 1];

const ModalNavigationButtons = ({
  showAchievementView,
  isPathCompletedLocal,
  getDisplayedContentLocal,
  modalNavigation,
  navigationContext,
  handleBackNavigationLocal,
  handleSaveAndQuit,
  getCurrentSubmissionStatus,
  getDisplayedContentTypeLocal,
  handleSubmitSubmission,
  userCurrentContent,
  submissionData,
  urlError,
  handleForwardBrowsingLocal,
  handleNextContent,
  nextContentLoading,
  progressLoading,
  isContentProgressing,
  canProgressFromSubmission,
  currentContentIndex,
  getNextButtonText,
  modalCourseContentLength,
  userSubmissionStatus,
  isSubmitting
}) => {
  if (showAchievementView || isPathCompletedLocal()) {
    return null;
  }

  const isAnyLoadingActive = nextContentLoading || progressLoading || isContentProgressing;

  const actualGlobalIndex = navigationContext?.currentGlobalIndex ?? -1;
  const showReturnToProgress = modalNavigation.isViewingBackwards &&
    actualGlobalIndex >= 0 &&
    modalNavigation.globalIndex >= actualGlobalIndex - 1;

  const canGoBack = getDisplayedContentLocal() && (
    modalNavigation.isViewingBackwards
      ? modalNavigation.canNavigateBack
      : (navigationContext?.currentGlobalIndex || 0) > 0
  );

  const submissionStatus = getCurrentSubmissionStatus();
  const displayedContent = getDisplayedContentLocal();
  const backwardsSubmissionStatus = modalNavigation.isViewingBackwards && displayedContent
    ? userSubmissionStatus?.[displayedContent._id] : null;
  const effectiveStatus = modalNavigation.isViewingBackwards ? backwardsSubmissionStatus : submissionStatus;

  const shouldShowSubmitButton = getDisplayedContentTypeLocal() === 'submissionForm' &&
    (!modalNavigation.isViewingBackwards
      ? (!effectiveStatus || !effectiveStatus.hasSubmission ||
         (effectiveStatus.hasSubmission && ['rejected', 'more_info_needed'].includes(effectiveStatus.submission.status)))
      : (effectiveStatus?.hasSubmission && ['rejected', 'more_info_needed'].includes(effectiveStatus.submission.status))
    );

  const activeContent = modalNavigation.isViewingBackwards ? displayedContent : userCurrentContent;
  const isSubmitDisabled =
    (activeContent?.linkRequired && !submissionData.link.trim()) ||
    (activeContent?.commentRequired && !submissionData.comment.trim()) ||
    (activeContent?.achievementImageRequired && (!submissionData.achievementImages || submissionData.achievementImages.length === 0)) ||
    (activeContent?.videoRequired && !submissionData.video) ||
    urlError;

  const nextButtonLabel = (() => {
    if (modalNavigation.isViewingBackwards) {
      return showReturnToProgress ? 'Return to Progress' : 'Next';
    }
    if (getDisplayedContentTypeLocal() === 'submissionForm' && submissionStatus?.hasSubmission) {
      if (submissionStatus.submission.status === 'approved') return 'Continue';
      if (submissionStatus.submission.status === 'pending') return 'Awaiting Approval';
      if (submissionStatus.submission.status === 'rejected') return 'Resubmit Required';
      if (submissionStatus.submission.status === 'more_info_needed') return 'Update Required';
    }
    return getNextButtonText();
  })();

  const isNextDisabled = isAnyLoadingActive ||
    (!modalNavigation.isViewingBackwards && (!userCurrentContent || !canProgressFromSubmission()));

  // Tape-measure trail — mirrors the header
  const total = modalCourseContentLength || 0;
  const done = (currentContentIndex ?? 0);
  const currentStep = done + 1;
  const MAX_TICKS = 24;
  const tickCount = total > MAX_TICKS ? MAX_TICKS : total;
  const ticks = Array.from({ length: tickCount }, (_, i) => {
    if (total === 0) return 'idle';
    const stepStart = (i / tickCount) * total;
    const stepEnd = ((i + 1) / tickCount) * total;
    if (currentStep > stepEnd) return 'done';
    if (currentStep > stepStart) return 'current';
    return 'idle';
  });

  return (
    <motion.div
      className="flex-shrink-0 bg-background"
      style={{ borderTop: '1px solid hsl(var(--border))' }}
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.55, delay: 0.15, ease: easing }}
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-3.5 flex items-center gap-4">

        {/* Left: ghost text Previous */}
        <div className="flex-1 flex justify-start">
          {canGoBack && (
            <button
              onClick={handleBackNavigationLocal}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-foreground/55 hover:text-foreground transition-colors"
            >
              <FaChevronLeft className="w-3 h-3" />
              <span className="hidden sm:inline">Previous</span>
            </button>
          )}
        </div>

        {/* Center: tape-measure trail + step caption */}
        {getDisplayedContentLocal() && total > 0 && (
          <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
            <div className="flex items-end gap-[5px] h-4">
              {ticks.map((state, i) => {
                if (state === 'current') {
                  return <span key={i} className="w-px bg-primary" style={{ height: '16px' }} />;
                }
                if (state === 'done') {
                  return <span key={i} className="w-px bg-primary/70" style={{ height: '10px' }} />;
                }
                return <span key={i} className="w-px bg-foreground/15" style={{ height: '6px' }} />;
              })}
            </div>
            <span className="text-[10px] font-bold tabular-nums tracking-[0.18em] text-foreground/55">
              {String(currentStep).padStart(2, '0')} <span className="text-foreground/25">/</span> {String(total).padStart(2, '0')}
            </span>
          </div>
        )}

        {/* Right: Save & Quit + primary */}
        <div className="flex-1 flex justify-end items-center gap-5">
          {getDisplayedContentLocal() && (
            <button
              onClick={handleSaveAndQuit}
              className="hidden sm:inline-flex items-center gap-1.5 text-[13px] font-semibold text-foreground/55 hover:text-foreground transition-colors"
            >
              Save & Quit
            </button>
          )}

          {shouldShowSubmitButton ? (
            <button
              onClick={() => handleSubmitSubmission(modalNavigation.isViewingBackwards ? displayedContent : undefined)}
              disabled={isSubmitDisabled || isSubmitting}
              className={`group inline-flex items-center gap-2 px-5 py-2.5 text-[13px] font-bold tracking-wide transition-all ${
                isSubmitDisabled || isSubmitting
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-mission text-mission-foreground hover:opacity-90'
              }`}
              style={{ borderRadius: '2px' }}
            >
              <FaClipboardList className="w-3.5 h-3.5" />
              <span>{isSubmitting ? 'Submitting...' : 'Submit Mission'}</span>
            </button>
          ) : (
            <button
              onClick={modalNavigation.isViewingBackwards ? handleForwardBrowsingLocal : handleNextContent}
              disabled={isNextDisabled}
              className={`group inline-flex items-center gap-2 px-5 py-2.5 text-[13px] font-bold tracking-wide transition-all ${
                isNextDisabled
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : modalNavigation.isViewingBackwards
                    ? 'bg-foreground text-background hover:opacity-90'
                    : 'bg-primary text-primary-foreground hover:opacity-90'
              }`}
              style={{ borderRadius: '2px' }}
            >
              {isAnyLoadingActive ? (
                <>
                  <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Loading</span>
                </>
              ) : (
                <>
                  {modalNavigation.isViewingBackwards && showReturnToProgress && (
                    <FaStepForward className="w-3.5 h-3.5" />
                  )}
                  <span>{nextButtonLabel}</span>
                  {!showReturnToProgress && (
                    <FaChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                  )}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ModalNavigationButtons;
